import React from "react";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";

/*  ---------------------------------------------------------------------
 *  Premium visual primitives (Phase 2 polish)
 *  ---------------------------------------------------------------------
 *  A single, tokenized set of building blocks the homepage — and later
 *  the marketing pages — use to feel like a cohesive premium SaaS
 *  surface instead of AI-template card soup. Everything here is pure
 *  presentation: no data fetching, no side-effects, no external state.
 *  Motion is respected only when the user hasn't asked for reduced
 *  motion (prefers-reduced-motion) — enforced with plain CSS classes.
 *  --------------------------------------------------------------------- */

/*  ---------------------------------------------------------------------
 *  PM_PALETTE — Postmoro homepage color system (single source of truth).
 *  Cool, analogous, professional. Only these hues appear as decorative
 *  UI accents on the homepage. Warm colors are reserved for demo image
 *  CONTENT (e.g. the taco creative photo). Amber is reserved for status
 *  ("Coming Soon" / "In Review") — never decorative.
 *  --------------------------------------------------------------------- */
export const PM_PALETTE = {
  teal:        "#1BA89F", // brand primary
  tealSoft:    "#5CD6C0", // lighter teal for glows / highlights
  tealDeep:    "#0E7490", // darker teal for depth
  cyan:        "#22D3EE", // bright cool accent
  blue:        "#2F8FE5", // cool secondary
  blueViolet:  "#7C6DE0", // sparingly used cool tertiary
  amberStatus: "#FFBE6E", // ONLY for Coming Soon / In Review / Needs Attention
};

// ------- EyebrowTag: single canonical eyebrow used once per section ---
export function EyebrowTag({ children, className = "", ...rest }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10.5px] uppercase tracking-[0.18em] " +
        "border border-[color:rgba(27,168,159,0.28)] bg-[color:rgba(27,168,159,0.08)] " +
        "text-[color:var(--brand-primary)] " + className
      }
      {...rest}
    >
      {children}
    </span>
  );
}

// ------- SectionBackdrop: SVG grid + one soft radial teal glow --------
// Rendered ONCE per major section behind the content. The radial hue
// is capped low so it never overpowers the copy.
export function SectionBackdrop({ variant = "grid", className = "" }) {
  return (
    <div
      aria-hidden="true"
      className={"pointer-events-none absolute inset-0 overflow-hidden " + className}
    >
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 30%, black 40%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 30%, black 40%, transparent 80%)",
        }}
      />
      {variant === "grid" ? (
        <div
          className="absolute -top-24 left-1/2 h-[520px] w-[900px] -translate-x-1/2 rounded-full opacity-[0.18]"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(27,168,159,0.55), rgba(27,168,159,0) 65%)",
            filter: "blur(48px)",
          }}
        />
      ) : null}
    </div>
  );
}

// ------- GlowPanel: 3 depth variants — flat / raised / featured ------
export function GlowPanel({
  variant = "raised",
  children,
  className = "",
  as: Tag = "div",
  ...rest
}) {
  const base =
    "relative rounded-2xl border transition-all duration-200 will-change-transform";
  const styles = {
    flat:
      "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)]",
    raised:
      "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] " +
      "shadow-[0_24px_46px_-32px_rgba(0,0,0,0.85),inset_0_1px_0_rgba(255,255,255,0.03)] " +
      "hover:border-[color:rgba(27,168,159,0.32)]",
    featured:
      "border-[color:rgba(27,168,159,0.55)] bg-[color:var(--surface-1)] " +
      "shadow-[0_28px_60px_-30px_rgba(27,168,159,0.35),inset_0_1px_0_rgba(255,255,255,0.05)]",
  };
  return (
    <Tag className={`${base} ${styles[variant] || styles.raised} ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

// ------- StatRing: SVG donut for usage / plan / capacity indicators --
export function StatRing({
  value = 0,
  max = 100,
  size = 56,
  label,
  sub,
  color = "var(--brand-primary)",
  className = "",
}) {
  const pct = Math.max(0, Math.min(1, max ? value / max : 0));
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className={"flex items-center gap-3 " + className}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={`${c * pct} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {(label || sub) && (
        <div className="min-w-0">
          {label && <div className="text-sm font-semibold text-[color:var(--text-1)] leading-tight">{label}</div>}
          {sub && <div className="text-[11px] text-[color:var(--text-3)] leading-tight mt-0.5">{sub}</div>}
        </div>
      )}
    </div>
  );
}

// ------- Sparkline: inline SVG mini-chart --------------------------
// Accepts a numeric array; renders a smoothed area path with subtle
// gradient fill. Purely presentational — homepage uses fixed demo
// arrays clearly labeled as "sample" so it can never be confused
// with real customer metrics.
export function Sparkline({ points = [], width = 140, height = 36, className = "" }) {
  if (!Array.isArray(points) || points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = Math.max(1, max - min);
  const step = width / (points.length - 1);
  const pts = points.map((p, i) => `${i * step},${height - ((p - min) / span) * (height - 6) - 3}`);
  const line = "M" + pts.join(" L");
  const area = line + ` L${width},${height} L0,${height} Z`;
  return (
    <svg width={width} height={height} className={className} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <defs>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(27,168,159,0.35)" />
          <stop offset="100%" stopColor="rgba(27,168,159,0)" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#sparkFill)" />
      <path d={line} fill="none" stroke="var(--brand-primary)" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ------- PlatformBadge: channel-native colored chip ----------------
const PLATFORM_META = {
  facebook:  { Icon: Facebook,  label: "Facebook",  color: "#1877F2", tint: "rgba(24,119,242,0.16)" },
  instagram: { Icon: Instagram, label: "Instagram", color: "#E1306C", tint: "rgba(225,48,108,0.16)" },
  linkedin:  { Icon: Linkedin,  label: "LinkedIn",  color: "#0A66C2", tint: "rgba(10,102,194,0.16)" },
  x:         { Icon: Twitter,   label: "X",         color: "#e5e7eb", tint: "rgba(229,231,235,0.10)" },
};
export function PlatformBadge({ platform, size = "md", variant = "native", className = "" }) {
  const meta = PLATFORM_META[platform] || PLATFORM_META.facebook;
  const { Icon, label, color, tint } = meta;
  const sizing = size === "sm"
    ? "h-6 px-2 text-[10px] gap-1"
    : "h-7 px-2.5 text-[11px] gap-1.5";

  // Postmoro variant — used on the marketing homepage so the badges
  // read as one cohesive Postmoro system instead of four competing
  // brand colors. Icon + label are preserved, only the fill/border/icon
  // color are neutralised to the Postmoro palette.
  if (variant === "postmoro") {
    return (
      <span
        className={
          "inline-flex items-center rounded-full font-medium " +
          sizing + " " + className +
          " bg-[color:var(--surface-2)] text-[color:var(--text-2)] " +
          "border border-[color:rgba(27,168,159,0.28)]"
        }
      >
        <Icon className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
        {label}
      </span>
    );
  }

  // Default: native platform colors (used everywhere else in the app —
  // Connections, Autopilot, Generate, dashboards, etc.).
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${sizing} ${className}`}
      style={{ background: tint, color, border: `1px solid ${color}33` }}
    >
      <Icon className="h-3.5 w-3.5" style={{ color }} />
      {label}
    </span>
  );
}
export { PLATFORM_META };

// ------- TacoTruckCreative: photographic campaign asset -----------
// Homepage hero demo creative. Renders the approved Cactus Comet
// Tacos "Taco Tuesday" campaign image from /public/demo. Explicitly
// labeled "Demo creative" via corner overlay so it can never be
// mistaken for real customer content.
export function TacoTruckCreative({ className = "" }) {
  return (
    <div className={"relative aspect-square overflow-hidden rounded-lg " + className}>
      {/* Base image */}
      <img
        src="/demo/cactus-comet-tacos-hero.png"
        alt="Cactus Comet Tacos — Taco Tuesday demo campaign creative"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
        draggable={false}
      />
      {/* Subtle Postmoro darkening — takes the raw ad-color intensity down a notch
          so the creative sits inside the dark Postmoro surface instead of shouting. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(11,13,16,0.15) 0%, rgba(11,13,16,0.05) 45%, rgba(11,13,16,0.55) 100%)",
        }}
      />
      {/* Teal inner ring — anchors the creative to the Postmoro palette. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none rounded-lg"
        style={{
          boxShadow:
            "inset 0 0 0 1px rgba(27,168,159,0.35), inset 0 0 40px -8px rgba(27,168,159,0.20)",
        }}
      />
      {/* Corner meta pills — Postmoro-toned (teal + neutral) instead of ad-orange. */}
      <div className="absolute left-3 bottom-3 right-3 flex items-end justify-between text-[10px] font-medium pointer-events-none">
        <div className="px-2 py-1 rounded-md bg-black/60 text-[color:var(--brand-primary)] backdrop-blur-sm border border-[color:rgba(27,168,159,0.35)] uppercase tracking-[0.14em]">
          Cactus Comet Tacos · Taco Tuesday
        </div>
        <div className="px-2 py-1 rounded-md bg-black/60 text-white/85 backdrop-blur-sm border border-white/10">
          Demo creative
        </div>
      </div>
    </div>
  );
}

// ------- PostPreviewFrame: realistic feed-post card mockup ---------
// Uses ONLY inert placeholder copy that reads clearly as a demo (no
// real names, no fake stats, no "5,241 likes"). The heart / comment
// icons are intentionally absent so nothing can be mistaken for a
// real engagement number.
export function PostPreviewFrame({
  platform = "linkedin",
  handle = "Postmoro",
  caption,
  imageColor = "linear-gradient(135deg,#1BA89F,#2F8FE5)",
  creative,
  className = "",
}) {
  const meta = PLATFORM_META[platform] || PLATFORM_META.linkedin;
  return (
    <GlowPanel variant="raised" className={"overflow-hidden " + className}>
      <div className="flex items-center gap-3 border-b border-[color:var(--border-subtle)] px-4 py-3">
        <div
          className="h-9 w-9 rounded-full flex items-center justify-center text-[color:var(--bg-0)] font-semibold text-sm"
          style={{ background: imageColor }}
        >P</div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[color:var(--text-1)] truncate">{handle}</div>
          <div className="text-[11px] text-[color:var(--text-3)] flex items-center gap-1.5">
            <PlatformBadge variant="postmoro" platform={platform} size="sm" />
            <span>· Preview</span>
          </div>
        </div>
      </div>
      <div className="px-4 py-3 text-sm text-[color:var(--text-1)] whitespace-pre-line leading-relaxed">
        {caption}
      </div>
      <div className="mx-4 mb-4">
        {creative ? creative : (
          <div className="aspect-[4/3] rounded-lg" style={{ background: imageColor, opacity: 0.85 }} />
        )}
      </div>
    </GlowPanel>
  );
}

// ------- WeekGrid: 7-column horizontal week visual -----------------
// Static placeholder data unless a `dots` array of length 7 is passed
// in. Each cell shows a small teal-tinted dot per scheduled slot.
// This lives here so Homepage + Autopilot + Dashboard all share the
// exact same visual language.
const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
export function WeekGrid({ slots = [1,0,2,1,1,0,2], activeIdx = 2, className = "" }) {
  return (
    <div className={"grid grid-cols-7 gap-2 " + className} aria-hidden="true">
      {DAYS.map((d, i) => {
        const active = i === activeIdx;
        return (
          <div
            key={d}
            className={
              "rounded-lg border p-2 min-h-[72px] flex flex-col " +
              (active
                ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.08)]"
                : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]")
            }
          >
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">{d}</div>
            <div className="mt-auto flex flex-wrap gap-1">
              {Array.from({ length: slots[i] || 0 }).map((_, j) => (
                <span key={j} className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--brand-primary)" }} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ------- ProgressRail: gradient bar with inset shadow -------------
export function ProgressRail({ value = 0, max = 100, className = "" }) {
  const pct = Math.max(0, Math.min(100, max ? (value / max) * 100 : 0));
  return (
    <div
      className={"h-1.5 rounded-full bg-[color:rgba(255,255,255,0.06)] " + className}
      role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}
    >
      <div
        className="h-full rounded-full"
        style={{
          width: pct + "%",
          background: "linear-gradient(90deg, " + PM_PALETTE.teal + ", " + PM_PALETTE.cyan + ")",
          boxShadow: "0 0 12px rgba(27,168,159,0.55)",
        }}
      />
    </div>
  );
}
